import {useLocalStorage} from 'platform/components';
import {z} from 'zod';

import {GetWorkshopOverviewApiArgSchema} from '@dms/api/workshop';

import type {WorkshopDashboardFilters} from '../types/WorkshopDashboardFilters';

const WorkshopDashboardFiltersSchema = GetWorkshopOverviewApiArgSchema.required().extend({
  orderTypes: z.array(z.object({id: z.string().uuid(), code: z.string().min(1)})).default([]),
});

const STORAGE_KEY = 'workshopDashboardFilters';
const EMPTY_FILTERS = {
  branchId: [],
  serviceOrderTypeId: [],
  serviceOrderVariantId: [],
  orderTypes: [],
};

export function useWorkshopDashboardFilters() {
  // The shared hook deserializes JSON without validating its runtime shape.
  const [storedFilters, setStoredFilters] = useLocalStorage<unknown>(STORAGE_KEY, EMPTY_FILTERS);
  const parsed = WorkshopDashboardFiltersSchema.safeParse(storedFilters);
  const restored = parsed.success ? parsed.data : EMPTY_FILTERS;
  const filters: WorkshopDashboardFilters = {
    branchId: restored.branchId,
    serviceOrderTypeId: restored.serviceOrderTypeId,
    serviceOrderVariantId: restored.serviceOrderVariantId,
  };

  const onFilterChange = (
    key: keyof WorkshopDashboardFilters,
    values: string[] | null,
    orderTypes: z.infer<typeof WorkshopDashboardFiltersSchema>['orderTypes'] = []
  ) => {
    setStoredFilters({
      ...restored,
      [key]: [...new Set(values ?? [])].sort(),
      orderTypes:
        key === 'serviceOrderTypeId'
          ? orderTypes.filter((type) => values?.includes(type.id))
          : restored.orderTypes,
    });
  };

  const onClearFilters = () => setStoredFilters(EMPTY_FILTERS);

  return {filters, orderTypes: restored.orderTypes, onFilterChange, onClearFilters};
}
